import type { BreedingCycleState } from "../domain/breeding";
import { breedingSeparationActionAllowed } from "../domain/breeding";
import type { OfflineOperation } from "../sync/types";
import {
  createOfflineOperation,
  type OperationClock,
  type OperationIdSource,
} from "../sync/operation";

export const HALL_PATROL_ACTIONS = [
  "feeding_record",
  "spawn_record",
  "egg_result_record",
  "free_swim_record",
  "parent_feeding_record",
  "parent_separation_record",
  "growout_record",
  "water_measurement",
  "water_change",
  "siphon",
  "treatment",
  "mortality",
  "health_observation",
  "task_complete",
] as const;

export type HallPatrolAction = (typeof HALL_PATROL_ACTIONS)[number];

export interface HallPatrolBreedingSnapshot {
  pairId: string;
  pairVersion: number;
  cycleId?: string;
  cycleVersion?: number;
  cycleState?: BreedingCycleState;
  separatedAt?: string;
}

export interface HallPatrolFeedingSnapshot {
  mode: "current" | "late";
  roundRunId: string;
  itemStatus: "pending" | "completed" | "missed" | "not_applicable";
}

export interface HallPatrolStockSnapshot {
  stockGroupId: string;
  stockVersion: number;
}

export interface HallPatrolTaskSnapshot {
  taskId: string;
  taskVersion: number;
  assignedToCurrentActor: boolean;
}

export interface HallPatrolTankSnapshot {
  farmId: string;
  tankId: string;
  tankCode: string;
  active: boolean;
  permissions: readonly string[];
  breeding?: HallPatrolBreedingSnapshot;
  feeding?: HallPatrolFeedingSnapshot;
  stock?: HallPatrolStockSnapshot;
  pendingTasks?: readonly HallPatrolTaskSnapshot[];
}

export interface HallPatrolQuickAction {
  action: HallPatrolAction;
  permission: string;
  commandName:
    | "feeding_record_v1"
    | "breeding_spawn_record_v1"
    | "breeding_egg_result_record_v1"
    | "breeding_free_swim_record_v1"
    | "breeding_stage_record_v1"
    | "hall_operation_record_v1"
    | "hall_task_complete_v1";
  operationType: string;
  mode?: string;
}

function hasPermission(snapshot: HallPatrolTankSnapshot, permission: string): boolean {
  return snapshot.permissions.includes(permission);
}

export function hallPatrolQuickActions(snapshot: HallPatrolTankSnapshot): HallPatrolQuickAction[] {
  if (!snapshot.active) {
    return [];
  }

  const actions: HallPatrolQuickAction[] = [];

  if (
    snapshot.feeding &&
    (snapshot.feeding.itemStatus === "pending" ||
      (snapshot.feeding.mode === "late" && snapshot.feeding.itemStatus === "missed")) &&
    hasPermission(snapshot, "feeding.record")
  ) {
    actions.push({
      action: "feeding_record",
      permission: "feeding.record",
      commandName: "feeding_record_v1",
      operationType: "feeding.record",
      mode: snapshot.feeding.mode,
    });
  }

  const breeding = snapshot.breeding;

  if (breeding && hasPermission(snapshot, "breeding.record")) {
    actions.push({
      action: "spawn_record",
      permission: "breeding.record",
      commandName: "breeding_spawn_record_v1",
      operationType: "breeding.spawn",
    });

    if (breeding.cycleId && breeding.cycleState === "eggs_pending") {
      actions.push({
        action: "egg_result_record",
        permission: "breeding.record",
        commandName: "breeding_egg_result_record_v1",
        operationType: "breeding.egg_result",
      });
    }

    if (breeding.cycleId && breeding.cycleState === "hatched") {
      actions.push({
        action: "free_swim_record",
        permission: "breeding.record",
        commandName: "breeding_free_swim_record_v1",
        operationType: "breeding.free_swim",
      });
    }

    if (breeding.cycleId && breeding.cycleState === "free_swim") {
      actions.push({
        action: "parent_feeding_record",
        permission: "breeding.record",
        commandName: "breeding_stage_record_v1",
        operationType: "breeding.stage",
        mode: "parent_feeding",
      });
    }

    if (
      breeding.cycleId &&
      breeding.cycleState &&
      breedingSeparationActionAllowed(breeding.cycleState)
    ) {
      actions.push({
        action: "parent_separation_record",
        permission: "breeding.record",
        commandName: "breeding_stage_record_v1",
        operationType: "breeding.stage",
        mode: "separated",
      });
    }

    if (
      breeding.cycleId &&
      breeding.separatedAt &&
      (breeding.cycleState === "free_swim" || breeding.cycleState === "parent_feeding")
    ) {
      actions.push({
        action: "growout_record",
        permission: "breeding.record",
        commandName: "breeding_stage_record_v1",
        operationType: "breeding.stage",
        mode: "growout",
      });
    }
  }

  if (hasPermission(snapshot, "water.record")) {
    actions.push(
      {
        action: "water_measurement",
        permission: "water.record",
        commandName: "hall_operation_record_v1",
        operationType: "hall.water_measurement",
      },
      {
        action: "water_change",
        permission: "water.record",
        commandName: "hall_operation_record_v1",
        operationType: "hall.water_change",
      },
    );
  }

  if (hasPermission(snapshot, "siphon.record")) {
    actions.push({
      action: "siphon",
      permission: "siphon.record",
      commandName: "hall_operation_record_v1",
      operationType: "hall.siphon",
    });
  }

  if (hasPermission(snapshot, "treatment.record")) {
    actions.push({
      action: "treatment",
      permission: "treatment.record",
      commandName: "hall_operation_record_v1",
      operationType: "hall.treatment",
    });
  }

  if (hasPermission(snapshot, "health.record")) {
    if (snapshot.stock) {
      actions.push({
        action: "mortality",
        permission: "health.record",
        commandName: "hall_operation_record_v1",
        operationType: "hall.mortality",
      });
    }

    actions.push({
      action: "health_observation",
      permission: "health.record",
      commandName: "hall_operation_record_v1",
      operationType: "hall.health_observation",
    });
  }

  if (
    hasPermission(snapshot, "tasks.record") &&
    snapshot.pendingTasks?.some((task) => task.assignedToCurrentActor)
  ) {
    actions.push({
      action: "task_complete",
      permission: "tasks.record",
      commandName: "hall_task_complete_v1",
      operationType: "task.complete",
    });
  }

  return actions;
}

export interface CreateHallPatrolOfflineOperationInput {
  action: HallPatrolQuickAction;
  snapshot: HallPatrolTankSnapshot;
  actorId: string;
  deviceId: string;
  performedAt: string;
  payload: Readonly<Record<string, unknown>>;
  baseVersion?: string;
}

export function createHallPatrolOfflineOperation(
  input: CreateHallPatrolOfflineOperationInput,
  options?: { clock?: OperationClock; idSource?: OperationIdSource },
): OfflineOperation {
  if (input.action.operationType.startsWith("qr.")) {
    throw new Error("QR_SEPARATE_BUSINESS_COMMAND_FORBIDDEN");
  }

  return createOfflineOperation(
    {
      operationType: input.action.operationType,
      schemaVersion: 1,
      actorId: input.actorId,
      farmId: input.snapshot.farmId,
      deviceId: input.deviceId,
      entityId: input.snapshot.tankId,
      performedAt: input.performedAt,
      ...(input.baseVersion === undefined ? {} : { baseVersion: input.baseVersion }),
      payload: {
        ...input.payload,
        commandName: input.action.commandName,
        tankId: input.snapshot.tankId,
        tankCode: input.snapshot.tankCode,
        ...(input.action.mode ? { mode: input.action.mode } : {}),
      },
    },
    options,
  );
}
